import { ref, watch, type Ref } from 'vue'

import {
  resolveClient,
  type ApolloClientLike,
} from '@/shared/ports/resolveClient'
import {
  RaidForestAssignmentRemoveDocument,
  RaidForestAssignmentUpsertDocument,
  RaidMapGroupPositionSetDocument,
  RaidMapPlanDocument,
  type RaidForestAssignmentRemoveMutation,
  type RaidForestAssignmentUpsertMutation,
  type RaidMapGroupPositionSetMutation,
  type RaidMapPlanQuery,
} from '../../lib/handWrittenGraphql.ts'
import { isForestLocationKey } from '../../lib/gvgMap.ts'
import type {
  ForestLocationKey,
  RaidForestAssignment,
  RaidId,
  RaidMapGroupPosition,
  RaidMapTreePosition,
} from '../../types/index.ts'
import { useRaidMapTreePosition } from './treePosition.ts'
import type { RaidMapTreePositionSnapshot } from './treePosition.ts'

export interface RaidMapCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly activeRaidId: Ref<RaidId | null>
}

export interface RaidMapApi {
  readonly groupPositions: Ref<ReadonlyArray<RaidMapGroupPosition>>
  readonly treePosition: Ref<RaidMapTreePosition | null>
  readonly forestAssignments: Ref<ReadonlyArray<RaidForestAssignment>>
  readonly loading: Ref<boolean>
  readonly error: Ref<string | null>
  readonly fetchRaidMapPlan: () => Promise<void>
  /** Apply the authoritative map rows bundled with a planner subscription. */
  readonly applyRaidMapPlan: (plan: RaidMapPlanSnapshot) => void
  readonly clearRaidMapPlan: () => void
  readonly setRaidMapGroupPosition: (
    raidGroupId: string,
    xBasisPoints: number,
    yBasisPoints: number,
  ) => Promise<void>
  readonly setRaidMapTreePosition: (
    xBasisPoints: number,
    yBasisPoints: number,
  ) => Promise<void>
  readonly upsertRaidForestAssignment: (
    raidPlayerId: string,
    locationKey: ForestLocationKey,
    spawnOffsetMinutes: number,
  ) => Promise<void>
  readonly removeRaidForestAssignment: (assignmentId: string) => Promise<void>
}

/**
 * Structural subscription shape. IDs stay string-or-number at the GraphQL
 * boundary and are normalized with the same code path as `RaidMapPlan`.
 */
export interface RaidMapPlanSnapshot {
  readonly groupPositions: ReadonlyArray<{
    readonly raidGroupId: string | number
    readonly xBasisPoints: number
    readonly yBasisPoints: number
  }>
  readonly treePosition: RaidMapTreePositionSnapshot | null
  readonly forestAssignments: ReadonlyArray<{
    readonly id: string | number
    readonly raidPlayerId: string | number
    readonly locationKey: string
    readonly spawnOffsetMinutes: number
  }>
}

interface QueuedGroupPosition {
  readonly raidGroupId: string
  readonly xBasisPoints: number
  readonly yBasisPoints: number
  readonly editGeneration: number
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function validBasisPoints(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 10_000
}

function validSpawnOffset(value: number): boolean {
  return Number.isInteger(value) && value > 0 && value % 5 === 0
}

export function useRaidMap(
  ctx: RaidMapCtx,
  client: ApolloClientLike = resolveClient(),
): RaidMapApi {
  const groupPositions = ref<ReadonlyArray<RaidMapGroupPosition>>([])
  const forestAssignments = ref<ReadonlyArray<RaidForestAssignment>>([])
  const loading = ref<boolean>(false)
  const error = ref<string | null>(null)
  const tree = useRaidMapTreePosition(ctx, client, error)
  let latestRequest = 0
  let scopeGeneration = 0
  let latestEditGeneration = 0
  const queuedGroupPositions = new Map<string, QueuedGroupPosition>()
  const groupPositionWorkers = new Map<string, Promise<void>>()
  const assignmentWorkers = new Map<number, Promise<void>>()

  function clearRaidMapPlan(): void {
    latestRequest += 1
    latestEditGeneration += 1
    scopeGeneration += 1
    groupPositions.value = []
    tree.clearTreePosition()
    forestAssignments.value = []
    loading.value = false
    error.value = null
  }

  // Map-plan rows are scoped to both the Discord guild and the selected raid.
  // Invalidate them synchronously when either owner changes so a newly selected
  // raid can never render the previous raid's canonical plan while its own
  // request is loading or after that request fails.
  watch(
    [ctx.activeDiscordGuildId, ctx.activeRaidId],
    clearRaidMapPlan,
    { flush: 'sync' },
  )

  function applyRaidMapPlan(plan: RaidMapPlanSnapshot): void {
    // An initial network-only query must not overwrite a newer subscription
    // delivery. Do not advance `latestEditGeneration`: queued writes still
    // need to finish and their own authoritative events will replace this
    // state in commit order.
    latestRequest += 1
    groupPositions.value = plan.groupPositions.map((position) => ({
      raidGroupId: String(position.raidGroupId),
      xBasisPoints: position.xBasisPoints,
      yBasisPoints: position.yBasisPoints,
    }))
    tree.applyTreePosition(plan.treePosition)
    forestAssignments.value = plan.forestAssignments
      .filter(
        (assignment): assignment is typeof assignment & {
          readonly locationKey: ForestLocationKey
        } => isForestLocationKey(assignment.locationKey),
      )
      .map((assignment) => ({
        id: String(assignment.id),
        raidPlayerId: String(assignment.raidPlayerId),
        locationKey: assignment.locationKey,
        spawnOffsetMinutes: assignment.spawnOffsetMinutes,
      }))
    loading.value = false
    error.value = null
  }

  async function fetchRaidMapPlan(): Promise<void> {
    const raidId = ctx.activeRaidId.value
    if (raidId === null) {
      clearRaidMapPlan()
      return
    }
    const guildId = ctx.activeDiscordGuildId.value
    const request = ++latestRequest
    const requestScope = scopeGeneration
    const requestEditGeneration = latestEditGeneration
    const requestTreeEditGeneration = tree.editGeneration()
    const isCurrent = (): boolean =>
      request === latestRequest
      && requestScope === scopeGeneration
      && requestEditGeneration === latestEditGeneration
      && requestTreeEditGeneration === tree.editGeneration()
      && ctx.activeDiscordGuildId.value === guildId
      && ctx.activeRaidId.value === raidId
    loading.value = true
    error.value = null
    try {
      const response = await client.query<RaidMapPlanQuery>({
        query: RaidMapPlanDocument,
        variables: {
          guildId,
          raidId,
        },
        fetchPolicy: 'network-only',
      })
      if (!isCurrent()) return
      const plan = response.data?.raidMapPlan
      if (plan === undefined) {
        groupPositions.value = []
        tree.applyTreePosition(null)
        forestAssignments.value = []
        error.value = 'The selected raid map is no longer available.'
        return
      }
      applyRaidMapPlan(plan)
    } catch (caught: unknown) {
      if (isCurrent()) error.value = errorMessage(caught)
    } finally {
      if (isCurrent()) loading.value = false
    }
  }

  function activeRaidOrError(): RaidId | null {
    const raidId = ctx.activeRaidId.value
    if (raidId === null) error.value = 'Select a raid before editing its map.'
    return raidId
  }

  function isCurrentScope(
    requestScope: number,
    guildId: string,
    raidId: RaidId,
  ): boolean {
    return requestScope === scopeGeneration
      && ctx.activeDiscordGuildId.value === guildId
      && ctx.activeRaidId.value === raidId
  }

  function applyOptimisticGroupPosition(position: QueuedGroupPosition): void {
    const nextPosition: RaidMapGroupPosition = {
      raidGroupId: position.raidGroupId,
      xBasisPoints: position.xBasisPoints,
      yBasisPoints: position.yBasisPoints,
    }
    const existingIndex = groupPositions.value.findIndex(
      (candidate) => candidate.raidGroupId === position.raidGroupId,
    )
    groupPositions.value = existingIndex === -1
      ? [...groupPositions.value, nextPosition]
      : groupPositions.value.map((candidate, index) =>
        index === existingIndex ? nextPosition : candidate,
      )
  }

  function reconcileFailedEdit(
    requestScope: number,
    guildId: string,
    raidId: RaidId,
    editGeneration: number,
    message: string,
  ): void {
    if (
      editGeneration === latestEditGeneration
      && isCurrentScope(requestScope, guildId, raidId)
    ) {
      error.value = message
    }
  }

  async function drainGroupPositionQueue(
    queueKey: string,
    requestScope: number,
    guildId: string,
    raidId: RaidId,
  ): Promise<void> {
    while (isCurrentScope(requestScope, guildId, raidId)) {
      const position = queuedGroupPositions.get(queueKey)
      if (position === undefined) return
      queuedGroupPositions.delete(queueKey)
      try {
        const response = await client.mutate<RaidMapGroupPositionSetMutation>({
          mutation: RaidMapGroupPositionSetDocument,
          variables: {
            guildId,
            raidId,
            raidGroupId: position.raidGroupId,
            xBasisPoints: position.xBasisPoints,
            yBasisPoints: position.yBasisPoints,
          },
        })
        if (!isCurrentScope(requestScope, guildId, raidId)) return
        if (position.editGeneration !== latestEditGeneration) continue
        if (response.data?.raidMapGroupPositionSet !== true) {
          reconcileFailedEdit(
            requestScope,
            guildId,
            raidId,
            position.editGeneration,
            'The group position could not be saved.',
          )
          continue
        }
      } catch (caught: unknown) {
        if (
          position.editGeneration === latestEditGeneration
          && isCurrentScope(requestScope, guildId, raidId)
        ) {
          reconcileFailedEdit(
            requestScope,
            guildId,
            raidId,
            position.editGeneration,
            errorMessage(caught),
          )
        }
      }
    }
  }

  async function setRaidMapGroupPosition(
    raidGroupId: string,
    xBasisPoints: number,
    yBasisPoints: number,
  ): Promise<void> {
    const raidId = activeRaidOrError()
    if (raidId === null) return
    if (!validBasisPoints(xBasisPoints) || !validBasisPoints(yBasisPoints)) {
      error.value = 'Map coordinates must be whole basis points from 0 to 10000.'
      return
    }
    const guildId = ctx.activeDiscordGuildId.value
    const requestScope = scopeGeneration
    const editGeneration = ++latestEditGeneration
    const queueKey = `${requestScope}:${raidGroupId}`
    const position: QueuedGroupPosition = {
      raidGroupId,
      xBasisPoints,
      yBasisPoints,
      editGeneration,
    }
    error.value = null
    applyOptimisticGroupPosition(position)
    queuedGroupPositions.set(queueKey, position)
    let worker = groupPositionWorkers.get(queueKey)
    if (worker === undefined) {
      const nextWorker = drainGroupPositionQueue(
        queueKey,
        requestScope,
        guildId,
        raidId,
      ).finally(() => {
        if (groupPositionWorkers.get(queueKey) === nextWorker) {
          groupPositionWorkers.delete(queueKey)
        }
      })
      groupPositionWorkers.set(queueKey, nextWorker)
      worker = nextWorker
    }
    await worker
  }

  function enqueueAssignmentWrite(
    requestScope: number,
    write: () => Promise<void>,
  ): Promise<void> {
    const previous = assignmentWorkers.get(requestScope) ?? Promise.resolve()
    const next = previous.then(write)
    assignmentWorkers.set(requestScope, next)
    void next.finally(() => {
      if (assignmentWorkers.get(requestScope) === next) {
        assignmentWorkers.delete(requestScope)
      }
    })
    return next
  }

  async function runAssignmentWrite(
    requestScope: number,
    guildId: string,
    raidId: RaidId,
    editGeneration: number,
    failureMessage: string,
    write: () => Promise<boolean>,
  ): Promise<void> {
    try {
      const succeeded = await write()
      if (!isCurrentScope(requestScope, guildId, raidId)) return
      if (editGeneration !== latestEditGeneration) return
      if (!succeeded) {
        reconcileFailedEdit(
          requestScope,
          guildId,
          raidId,
          editGeneration,
          failureMessage,
        )
        return
      }
    } catch (caught: unknown) {
      if (
        editGeneration === latestEditGeneration
        && isCurrentScope(requestScope, guildId, raidId)
      ) {
        reconcileFailedEdit(
          requestScope,
          guildId,
          raidId,
          editGeneration,
          errorMessage(caught),
        )
      }
    }
  }

  async function upsertRaidForestAssignment(
    raidPlayerId: string,
    locationKey: ForestLocationKey,
    spawnOffsetMinutes: number,
  ): Promise<void> {
    const raidId = activeRaidOrError()
    if (raidId === null) return
    if (!validSpawnOffset(spawnOffsetMinutes)) {
      error.value = 'Forest spawn times must be positive five-minute offsets.'
      return
    }
    const guildId = ctx.activeDiscordGuildId.value
    const requestScope = scopeGeneration
    const editGeneration = ++latestEditGeneration
    error.value = null
    await enqueueAssignmentWrite(requestScope, () => runAssignmentWrite(
      requestScope,
      guildId,
      raidId,
      editGeneration,
      'The forest assignment could not be saved.',
      async () => {
        const response = await client.mutate<RaidForestAssignmentUpsertMutation>({
          mutation: RaidForestAssignmentUpsertDocument,
          variables: {
            guildId,
            raidId,
            raidPlayerId,
            locationKey,
            spawnOffsetMinutes,
          },
        })
        return response.data?.raidForestAssignmentUpsert === true
      },
    ))
  }

  async function removeRaidForestAssignment(assignmentId: string): Promise<void> {
    const raidId = activeRaidOrError()
    if (raidId === null) return
    const guildId = ctx.activeDiscordGuildId.value
    const requestScope = scopeGeneration
    const editGeneration = ++latestEditGeneration
    error.value = null
    await enqueueAssignmentWrite(requestScope, () => runAssignmentWrite(
      requestScope,
      guildId,
      raidId,
      editGeneration,
      'The forest assignment could not be removed.',
      async () => {
        const response = await client.mutate<RaidForestAssignmentRemoveMutation>({
          mutation: RaidForestAssignmentRemoveDocument,
          variables: {
            guildId,
            raidId,
            assignmentId,
          },
        })
        return response.data?.raidForestAssignmentRemove === true
      },
    ))
  }

  return {
    groupPositions,
    treePosition: tree.treePosition,
    forestAssignments,
    loading,
    error,
    fetchRaidMapPlan,
    applyRaidMapPlan,
    clearRaidMapPlan,
    setRaidMapGroupPosition,
    setRaidMapTreePosition: tree.setRaidMapTreePosition,
    upsertRaidForestAssignment,
    removeRaidForestAssignment,
  }
}
